/**
 * @file Map-marker-mini-map.tsx
 * @module MapMarkerMiniMap
 * @description Mini kartes komponente marķieru rediģēšanas formai (jaunā versija).
 *              Izveido atsevišķu OpenLayers karti (MINIMAP) ar Pamatkarte (Mapbox GL),
 *              izcēluma slāni un Modify interakciju atlasītā marķiera rediģēšanai.
 *              MVT datu slāņi tiek pievienoti kā konteksts no ĢIS datu servisa.
 */
import View from "ol/View";
import { MARKER_LAYER } from "../Map-marker";
import { fromLonLat, toLonLat } from "ol/proj";
import { Feature, getUid, Map } from "ol";
import { useContext, useEffect, useRef } from "react";
import { Map as MapboxMap } from "mapbox-gl";
import Layer from "ol/layer/Layer.js";
import Source from "ol/source/Source.js";
import { defaults } from "ol/control";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { getHighlightStyle } from "../controls/Draw-styles";
import { NewFeatureListContext } from "./Map-marker-list-wrapper";
import { Modify } from "ol/interaction";
import { getLayerDef } from "./Layer-loader";
import { DataResponse } from "../../store/Layers.repository";
import { MapMarkerLayerManager } from "../Map-marker-layer-manager";
import { OverlayWrapper } from "../../map/overlay/Overlay-wrapper";
import { ClassifierComponent } from "../../classifiers/Classifiers";

export const MINI_MARKER_LAYER = new VectorLayer({
	source: new VectorSource(),
	zIndex: Infinity,
	style: (f, r) => getHighlightStyle(f as Feature, r),
});

const MODIFY = new Modify({
	source: MINI_MARKER_LAYER.getSource()!,
	style: (f, r) => getHighlightStyle(f as Feature, r),
});

const createMap = () => {
	return new Map({
		view: new View({
			center: fromLonLat([24.1056, 56.9677]),
			zoom: 8,
			maxZoom: 19,
		}),
		controls: defaults({ attribution: false, zoom: false, rotate: false }),
		layers: [MINI_MARKER_LAYER],
	});
};

export const MINIMAP = createMap();

export const MapMarkerMiniMap = () => {
	const { selectedItem } = useContext(NewFeatureListContext);
	const mapElement = useRef<HTMLDivElement | null>(null);
	const dataRef = useRef<DataResponse[]>();

	useEffect(() => {
		const source = MINI_MARKER_LAYER.getSource()!;
		source.clear();

		if (selectedItem) source.addFeature(selectedItem);
	}, [selectedItem]);

	useEffect(() => {
		getLayerDef().then((res) => {
			dataRef.current = res.data;

			const baseDef = res.base.find((l) => l.enabled)!;
			if (baseDef) {
				const mbMap = new MapboxMap({
					style: baseDef.parameters,
					attributionControl: false,
					boxZoom: false,
					container: mapElement.current!,
					doubleClickZoom: false,
					dragPan: false,
					maxZoom: 19,
					dragRotate: false,
					interactive: false,
					keyboard: false,
					pitchWithRotate: false,
					scrollZoom: false,
					touchZoomRotate: false,
				});

				const mbLayer = new Layer({
					zIndex: -1000,
					render: function (frameState) {
						const canvas = mbMap.getCanvas();
						const viewState = frameState.viewState;

						const visible = mbLayer.getVisible();
						canvas.style.display = visible ? "block" : "none";
						canvas.style.position = "absolute";

						const opacity = mbLayer.getOpacity();
						//@ts-ignore
						canvas.style.opacity = opacity;

						// adjust view parameters in mapbox
						const rotation = viewState.rotation;
						mbMap.jumpTo({
							center: toLonLat(viewState.center) as any,
							zoom: viewState.zoom - 1,
							bearing: (-rotation * 180) / Math.PI,
						});

						// NOTE: THIS MIGHT BREAK IF UPDATING THE MAPBOX VERSION
						//@ts-ignore
						if (mbMap._frame) {
							//@ts-ignore
							mbMap._frame.cancel();
							//@ts-ignore
							mbMap._frame = null;
						} //@ts-ignore
						mbMap._render();

						return canvas;
					},
					source: new Source({
						attributions: [
							'<a href="https://www.maptiler.com/copyright/" target="_blank">© MapTiler</a>',
							'<a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>',
						],
					}),
				});

				MINIMAP.addLayer(mbLayer);
				MINIMAP.setTarget(mapElement.current!);

				MINIMAP.addInteraction(MODIFY);
			}
		});

		const keyEvent = MODIFY.on("modifyend", (event) => {
			const [feature] = event.features.getArray();
			const f = MARKER_LAYER.getSource()!.getFeatureByUid(getUid(feature));

			if (f) {
				const props = f.getProperties();

				f.setProperties({
					updatedlr: false,
					trigger: !props["trigger"],
					DONT_UPDATE: false,
				});
			}
		});

		return () => MODIFY.un("modifyend", keyEvent.listener);
	}, [mapElement]);

	return (
		<div className="w-full h-[260px] my-2" ref={mapElement}>
			{MINIMAP && dataRef.current && <MapMarkerLayerManager map={MINIMAP} data={dataRef.current} />}
			{MINIMAP && <OverlayWrapper map={MINIMAP}></OverlayWrapper>}
			{MINIMAP && <ClassifierComponent></ClassifierComponent>}
		</div>
	);
};
